import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { Link as RouterLink } from '@tanstack/react-router';
import {
  deriveWorkflowProgress,
  type Model,
  type Video,
  type WorkflowStepId,
  type WorkflowStepState,
} from '@ddp/shared';
import Loading from '@carbon/react/es/components/Loading/Loading';
import { InlineNotification } from '@carbon/react/es/components/Notification/Notification';
import CloudUpload from '@carbon/icons-react/es/CloudUpload';
import Tag from '@carbon/icons-react/es/Tag';
import MachineLearningModel from '@carbon/icons-react/es/MachineLearningModel';
import Analytics from '@carbon/icons-react/es/Analytics';
import Rocket from '@carbon/icons-react/es/Rocket';
import Checkmark from '@carbon/icons-react/es/Checkmark';
import Locked from '@carbon/icons-react/es/Locked';
import ArrowRight from '@carbon/icons-react/es/ArrowRight';

import { apiFetch } from '../api/client.js';
import { useT, type MessageKey } from '../i18n/index.js';
import { PageHeader } from '../components/PageHeader.js';

/**
 * Cap d'une étape vers la zone où elle se joue. `hash` cible une section ancrée (ADR-0017), pour
 * déposer l'utilisateur exactement au bon endroit (p. ex. la liste de ses modèles).
 */
interface StepMeta {
  id: WorkflowStepId;
  icon: ComponentType<{ size?: number }>;
  to: string;
  hash?: string;
  titleKey: MessageKey;
  bodyKey: MessageKey;
  actionKey: MessageKey;
}

// L'ordre suit celui du contrat partagé `WORKFLOW_STEP_IDS` (ADR-0020).
const STEP_META: StepMeta[] = [
  {
    id: 'data',
    icon: CloudUpload,
    to: '/data',
    titleKey: 'workflow.step.data.title',
    bodyKey: 'workflow.step.data.body',
    actionKey: 'workflow.step.data.action',
  },
  {
    id: 'annotation',
    icon: Tag,
    to: '/data',
    titleKey: 'workflow.step.annotation.title',
    bodyKey: 'workflow.step.annotation.body',
    actionKey: 'workflow.step.annotation.action',
  },
  {
    id: 'model',
    icon: MachineLearningModel,
    to: '/model',
    hash: 'vos-modeles',
    titleKey: 'workflow.step.model.title',
    bodyKey: 'workflow.step.model.body',
    actionKey: 'workflow.step.model.action',
  },
  {
    id: 'evaluation',
    icon: Analytics,
    to: '/model',
    hash: 'vos-modeles',
    titleKey: 'workflow.step.evaluation.title',
    bodyKey: 'workflow.step.evaluation.body',
    actionKey: 'workflow.step.evaluation.action',
  },
  {
    id: 'deploy',
    icon: Rocket,
    to: '/deploy',
    titleKey: 'workflow.step.deploy.title',
    bodyKey: 'workflow.step.deploy.body',
    actionKey: 'workflow.step.deploy.action',
  },
];

/**
 * Parcours guidé « du vol au modèle déployé » (ADR-0020). L'avancement n'est pas stocké : il se
 * déduit des données de l'organisation via le contrat partagé `deriveWorkflowProgress`. La page
 * lit vidéos et modèles (endpoints cloisonnés), en tire les signaux, et met en avant l'étape à
 * reprendre.
 */
export function WorkflowPage() {
  const t = useT();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [videos, setVideos] = useState<Video[]>([]);
  const [models, setModels] = useState<Model[]>([]);

  useEffect(() => {
    let active = true;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const [v, m] = await Promise.all([
          apiFetch<Video[]>('/videos'),
          apiFetch<Model[]>('/models'),
        ]);
        if (!active) return;
        setVideos(v);
        setModels(m);
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'Chargement impossible');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const signals = useMemo(
    () => ({
      videoCount: videos.length,
      annotatedVideoCount: videos.filter((v) => v.annotationCount > 0).length,
      readyModelCount: models.filter((m) => m.status === 'READY').length,
      evaluatedModelCount: models.filter(
        (m) => m.metrics && typeof (m.metrics as { mAP?: number }).mAP === 'number',
      ).length,
    }),
    [videos, models],
  );
  const progress = useMemo(() => deriveWorkflowProgress(signals), [signals]);

  if (loading) {
    return (
      <section className="ddp-page__section">
        <Loading withOverlay={false} description={t('common.loading')} />
      </section>
    );
  }

  // Décompte à afficher sur une étape terminée : la donnée qui la valide, pour un « fait » tangible.
  const doneSummary = (id: WorkflowStepId): string | null => {
    switch (id) {
      case 'data':
        return `${signals.videoCount} ${t('workflow.count.videos')}`;
      case 'annotation':
        return `${signals.annotatedVideoCount} ${t('workflow.count.annotated')}`;
      case 'model':
        return `${signals.readyModelCount} ${t('workflow.count.models')}`;
      case 'evaluation':
        return `${signals.evaluatedModelCount} ${t('workflow.count.evaluated')}`;
      default:
        return null;
    }
  };

  const stateById = new Map(progress.steps.map((s) => [s.id, s.state]));
  const allActionableDone = progress.currentStepId === null;
  const ratio = `${progress.completedCount}/${progress.actionableCount} ${t('workflow.progress')}`;

  return (
    <section className="ddp-page__section ddp-workflow">
      <PageHeader title={t('workflow.title')} lead={t('workflow.intro')} />

      {error && (
        <InlineNotification
          kind="error"
          title={t('common.error')}
          subtitle={error}
          lowContrast
          hideCloseButton
        />
      )}

      {/* Barre d'avancement : ratio d'étapes actionnables terminées (le déploiement, verrouillé,
          n'entre pas dans le compte). */}
      <div className="ddp-workflow__meter">
        <div className="ddp-workflow__meter-head">
          <span className="ddp-workflow__meter-label">{ratio}</span>
          {allActionableDone && (
            <span className="ddp-workflow__meter-done">{t('workflow.allDone.title')}</span>
          )}
        </div>
        <div
          className="ddp-workflow__meter-track"
          role="progressbar"
          aria-valuenow={progress.completedCount}
          aria-valuemin={0}
          aria-valuemax={progress.actionableCount}
        >
          <span
            style={{
              width: `${(progress.completedCount / progress.actionableCount) * 100}%`,
            }}
          />
        </div>
      </div>

      <ol className="ddp-workflow__steps">
        {STEP_META.map((meta, i) => {
          const state = (stateById.get(meta.id) ?? 'todo') as WorkflowStepState;
          const Icon = meta.icon;
          const summary = state === 'done' ? doneSummary(meta.id) : null;
          const isLast = i === STEP_META.length - 1;
          return (
            <li key={meta.id} className={`ddp-wstep ddp-wstep--${state}`}>
              {/* Colonne repère : pastille numérotée / cochée / verrouillée + filet de liaison. */}
              <div className="ddp-wstep__rail">
                <span className="ddp-wstep__marker" aria-hidden="true">
                  {state === 'done' ? (
                    <Checkmark size={20} />
                  ) : state === 'locked' ? (
                    <Locked size={16} />
                  ) : (
                    <span className="ddp-wstep__num">{i + 1}</span>
                  )}
                </span>
                {!isLast && <span className="ddp-wstep__line" aria-hidden="true" />}
              </div>

              <div className="ddp-wstep__card">
                <div className="ddp-wstep__head">
                  <Icon size={20} />
                  <h2 className="ddp-wstep__title">{t(meta.titleKey)}</h2>
                  <span className={`ddp-wstep__chip ddp-wstep__chip--${state}`}>
                    {summary ??
                      t(
                        (
                          {
                            done: 'workflow.state.done',
                            current: 'workflow.state.current',
                            todo: 'workflow.state.todo',
                            locked: 'workflow.state.locked',
                          } as const
                        )[state],
                      )}
                  </span>
                </div>
                <p className="ddp-wstep__body">{t(meta.bodyKey)}</p>

                {/* L'action n'apparaît que là où elle a du sens : l'étape courante l'affiche en plein,
                    une étape faite propose de la revisiter, une étape verrouillée n'offre rien. */}
                {state === 'current' && (
                  <RouterLink
                    to={meta.to}
                    hash={meta.hash}
                    className="ddp-wstep__action ddp-wstep__action--primary"
                  >
                    {t(meta.actionKey)}
                    <ArrowRight size={16} />
                  </RouterLink>
                )}
                {state === 'done' && (
                  <RouterLink to={meta.to} hash={meta.hash} className="ddp-wstep__action">
                    {t('workflow.revisit')}
                    <ArrowRight size={16} />
                  </RouterLink>
                )}
                {state === 'todo' && (
                  <RouterLink to={meta.to} hash={meta.hash} className="ddp-wstep__action">
                    {t(meta.actionKey)}
                    <ArrowRight size={16} />
                  </RouterLink>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {allActionableDone && <p className="ddp-workflow__outro">{t('workflow.allDone.desc')}</p>}
    </section>
  );
}
